import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  DatePicker,
  InputNumber,
  Space,
  Tag,
  Popconfirm,
  message,
  Row,
  Col,
  Statistic,
  Divider,
  Upload,
  Tooltip
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
  FileExcelOutlined,
  FilePdfOutlined,
  AppstoreOutlined,
  UploadOutlined,
  DollarOutlined,
  CalendarOutlined,
  QrcodeOutlined
} from '@ant-design/icons';
import moment from 'moment';
import axios from 'axios';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Layout from '../../layout/Layout';
import Header from '../../components/Header';
import { getHeaders } from '../../utility/getHeader';
import fetchData from '../../functions/fetchData';

const { Option } = Select;
const { TextArea } = Input;

// Asset status options (matching backend ENUM)
const ASSET_STATUS = [
  { value: 'Active', label: 'Active', color: 'success' },
  { value: 'Inactive', label: 'Inactive', color: 'default' },
  { value: 'Under Maintenance', label: 'Under Maintenance', color: 'warning' },
  { value: 'Disposed', label: 'Disposed', color: 'error' },
  { value: 'Lost', label: 'Lost', color: 'error' }
];

// Asset condition options
const ASSET_CONDITION = [
  { value: 'Excellent', label: 'Excellent', color: 'success' },
  { value: 'Good', label: 'Good', color: 'blue' },
  { value: 'Fair', label: 'Fair', color: 'warning' },
  { value: 'Poor', label: 'Poor', color: 'error' }
];

export default function Assets() {
  const [assets, setAssets] = useState([]);
  const [filteredAssets, setFilteredAssets] = useState([]);
  const [categories, setCategories] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingAsset, setEditingAsset] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [form] = Form.useForm();

  // Statistics
  const [statistics, setStatistics] = useState({
    totalAssets: 0,
    totalValue: 0,
    activeAssets: 0,
    maintenanceAssets: 0
  });

  useEffect(() => {
    fetchAssets();
    fetchCategories();
    fetchEmployees();
  }, []);

  useEffect(() => {
    filterAssets();
  }, [assets, searchText, categoryFilter, statusFilter]);

  const fetchAssets = async () => {
    setLoading(true);
    try {
      const response = await axios.get('/assets', getHeaders());
      if (response.data.success) {
        const assetsData = response.data.data || [];
        setAssets(assetsData);
        calculateStatistics(assetsData);
      }
    } catch (error) {
      console.error('Error fetching assets:', error);
      message.error('Failed to load assets');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get('/assets/categories', getHeaders());
      if (response.data.success) {
        setCategories(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
      message.error('Failed to load categories');
    }
  };

  const fetchEmployees = async () => {
    try {
      const employeesData = await fetchData('employees', null, 'id', {});
      if (employeesData && Array.isArray(employeesData)) {
        setEmployees(employeesData);
      }
    } catch (error) {
      console.error('Error fetching employees:', error);
      message.error('Failed to load employees');
    }
  };

  const calculateStatistics = (data) => {
    const stats = {
      totalAssets: data.length,
      totalValue: data.reduce((sum, asset) => sum + (parseFloat(asset.current_value) || 0), 0),
      activeAssets: data.filter(a => a.status === 'Active').length,
      maintenanceAssets: data.filter(a => a.status === 'Under Maintenance').length
    };
    setStatistics(stats);
  };

  const filterAssets = () => {
    let filtered = [...assets];

    // Search filter
    if (searchText) {
      filtered = filtered.filter(asset =>
        asset.asset_name?.toLowerCase().includes(searchText.toLowerCase()) ||
        asset.asset_code?.toLowerCase().includes(searchText.toLowerCase()) ||
        asset.description?.toLowerCase().includes(searchText.toLowerCase())
      );
    }

    // Category filter
    if (categoryFilter !== 'all') {
      filtered = filtered.filter(asset => asset.category_id === parseInt(categoryFilter));
    }

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(asset => asset.status === statusFilter);
    }

    setFilteredAssets(filtered);
  };

  const handleAdd = () => {
    setEditingAsset(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (record) => {
    setEditingAsset(record);
    form.setFieldsValue({
      ...record,
      purchase_date: record.purchase_date ? moment(record.purchase_date) : null,
      warranty_expiry: record.warranty_expiry ? moment(record.warranty_expiry) : null,
      last_maintenance_date: record.last_maintenance_date ? moment(record.last_maintenance_date) : null,
      next_maintenance_date: record.next_maintenance_date ? moment(record.next_maintenance_date) : null
    });
    setModalVisible(true);
  };

  // Auto-generate asset code from category and asset name
  const generateAssetCode = (categoryId, assetName) => {
    if (!assetName || assetName.trim() === '') return '';
    
    // Get category prefix
    let categoryPrefix = '';
    if (categoryId) {
      const category = categories.find(c => c.id === categoryId);
      if (category) {
        // Use category short code or fall back to first 3 letters
        categoryPrefix = category.short_code || category.category_name.substring(0, 3).toUpperCase();
      }
    }
    
    // Generate code from asset name
    const words = assetName.trim().split(/\s+/);
    let nameCode = '';
    
    if (words.length === 1) {
      // Single word: take first 4 characters
      nameCode = words[0].substring(0, 4).toUpperCase();
    } else {
      // Multiple words: take first letter of each word
      nameCode = words.map(word => word.charAt(0).toUpperCase()).join('');
    }
    
    // Add random 2-digit number
    const randomNum = Math.floor(Math.random() * 100).toString().padStart(2, '0');
    
    // Combine: CATEGORY-NAMECODE+NUMBER (e.g., ELE-LAPT45 or IT-DL32)
    return categoryPrefix ? `${categoryPrefix}-${nameCode}${randomNum}` : `${nameCode}${randomNum}`;
  };

  // Watch for form changes to generate asset code
  const handleFormChange = (changedValues, allValues) => {
    // Generate code when asset name or category changes (for both new and edit)
    if (changedValues.asset_name !== undefined || changedValues.category_id !== undefined) {
      const generatedCode = generateAssetCode(allValues.category_id, allValues.asset_name);
      form.setFieldsValue({ asset_code: generatedCode });
    }
  };

  const handleDelete = async (id) => {
    try {
      const response = await axios.delete(`/assets/${id}`, getHeaders());
      if (response.data.success) {
        message.success('Asset deleted successfully');
        fetchAssets();
      }
    } catch (error) {
      console.error('Error deleting asset:', error);
      message.error('Failed to delete asset');
    }
  };

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      const formData = {
        ...values,
        purchase_date: values.purchase_date ? values.purchase_date.format('YYYY-MM-DD') : null,
        warranty_expiry: values.warranty_expiry ? values.warranty_expiry.format('YYYY-MM-DD') : null,
        last_maintenance_date: values.last_maintenance_date ? values.last_maintenance_date.format('YYYY-MM-DD') : null,
        next_maintenance_date: values.next_maintenance_date ? values.next_maintenance_date.format('YYYY-MM-DD') : null,
        current_value: values.current_value || values.purchase_price || 0
      };

      let response;
      if (editingAsset) {
        response = await axios.put(`/assets/${editingAsset.id}`, formData, getHeaders());
      } else {
        response = await axios.post('/assets', formData, getHeaders());
      }

      if (response.data.success) {
        message.success(`Asset ${editingAsset ? 'updated' : 'added'} successfully`);
        setModalVisible(false);
        form.resetFields();
        fetchAssets();
      }
    } catch (error) {
      console.error('Error saving asset:', error);
      message.error(`Failed to ${editingAsset ? 'update' : 'add'} asset`);
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      title: 'Asset Code',
      dataIndex: 'asset_code',
      key: 'asset_code',
      width: 120,
      render: (text) => <Tag icon={<QrcodeOutlined />} color="blue">{text}</Tag>
    },
    {
      title: 'Asset Name',
      dataIndex: 'asset_name',
      key: 'asset_name',
      width: 200,
      sorter: (a, b) => a.asset_name.localeCompare(b.asset_name)
    },
    {
      title: 'Category',
      dataIndex: 'category_name',
      key: 'category_name',
      width: 150,
      render: (category) => <Tag color="blue">{category}</Tag>
    },
    {
      title: 'Brand/Model',
      key: 'brand_model',
      width: 150,
      render: (_, record) => (
        <span>{record.brand ? `${record.brand} ${record.model || ''}` : '-'}</span>
      )
    },
    {
      title: 'Current Value',
      dataIndex: 'current_value',
      key: 'current_value',
      width: 130,
      render: (value) => `฿${parseFloat(value || 0).toLocaleString('th-TH', { minimumFractionDigits: 2 })}`,
      sorter: (a, b) => parseFloat(a.current_value || 0) - parseFloat(b.current_value || 0)
    },
    {
      title: 'Purchase Date',
      dataIndex: 'purchase_date',
      key: 'purchase_date',
      width: 120,
      render: (date) => date ? moment(date).format('DD/MM/YYYY') : '-'
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 130,
      render: (status) => {
        const statusObj = ASSET_STATUS.find(s => s.value === status);
        return <Tag color={statusObj?.color}>{statusObj?.label || status}</Tag>;
      }
    },
    {
      title: 'Location',
      dataIndex: 'location',
      key: 'location',
      width: 150
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 120,
      fixed: 'right',
      render: (_, record) => (
        <Space>
          <Tooltip title="Edit">
            <Button
              type="primary"
              size="small"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
            />
          </Tooltip>
          <Tooltip title="Delete">
            <Popconfirm
              title="Are you sure to delete this asset?"
              onConfirm={() => handleDelete(record.id)}
              okText="Yes"
              cancelText="No"
            >
              <Button
                danger
                size="small"
                icon={<DeleteOutlined />}
              />
            </Popconfirm>
          </Tooltip>
        </Space>
      )
    }
  ];

  return (
    <Layout>
      <Header title="Asset Management" />
      <ToastContainer />

      {/* Statistics Cards */}
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col xs={24} sm={12} md={6}>
          <Card style={{ 
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            borderRadius: '12px',
            border: 'none',
            boxShadow: '0 4px 15px rgba(102, 126, 234, 0.3)'
          }}>
            <Statistic
              title={<span style={{ color: '#000', opacity: 0.9 }}>Total Assets</span>}
              value={statistics.totalAssets}
              prefix={<AppstoreOutlined style={{ color: '#000' }} />}
              valueStyle={{ color: '#000', fontWeight: 'bold' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card style={{ 
            background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
            borderRadius: '12px',
            border: 'none',
            boxShadow: '0 4px 15px rgba(240, 147, 251, 0.3)'
          }}>
            <Statistic
              title={<span style={{ color: '#000', opacity: 0.9 }}>Total Value</span>}
              value={statistics.totalValue}
              prefix={<DollarOutlined style={{ color: '#000' }} />}
              suffix={<span style={{ color: '#000' }}>THB</span>}
              precision={2}
              valueStyle={{ color: '#000', fontWeight: 'bold' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card style={{ 
            background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
            borderRadius: '12px',
            border: 'none',
            boxShadow: '0 4px 15px rgba(79, 172, 254, 0.3)'
          }}>
            <Statistic
              title={<span style={{ color: '#000', opacity: 0.9 }}>Active Assets</span>}
              value={statistics.activeAssets}
              prefix={<span style={{ fontSize: '20px' }}>✅</span>}
              valueStyle={{ color: '#000', fontWeight: 'bold' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card style={{ 
            background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
            borderRadius: '12px',
            border: 'none',
            boxShadow: '0 4px 15px rgba(250, 112, 154, 0.3)'
          }}>
            <Statistic
              title={<span style={{ color: '#000', opacity: 0.9 }}>Under Maintenance</span>}
              value={statistics.maintenanceAssets}
              prefix={<span style={{ fontSize: '20px' }}>⚠️</span>}
              valueStyle={{ color: '#000', fontWeight: 'bold' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Main Table Card */}
      <Card
        title={
          <Space>
            <AppstoreOutlined />
            <span>Organization Assets</span>
          </Space>
        }
        extra={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleAdd}
          >
            Add New Asset
          </Button>
        }
      >
        {/* Filters */}
        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col xs={24} sm={8} md={6}>
            <Input
              placeholder="Search assets..."
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              allowClear
            />
          </Col>
          <Col xs={24} sm={8} md={6}>
            <Select
              style={{ width: '100%' }}
              placeholder="Filter by Category"
              value={categoryFilter}
              onChange={setCategoryFilter}
            >
              <Option value="all">All Categories</Option>
              {categories.map(cat => (
                <Option key={cat.id} value={cat.id}>{cat.category_name}</Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} sm={8} md={6}>
            <Select
              style={{ width: '100%' }}
              placeholder="Filter by Status"
              value={statusFilter}
              onChange={setStatusFilter}
            >
              <Option value="all">All Status</Option>
              {ASSET_STATUS.map(status => (
                <Option key={status.value} value={status.value}>{status.label}</Option>
              ))}
            </Select>
          </Col>
        </Row>

        <Table
          columns={columns}
          dataSource={filteredAssets}
          loading={loading}
          rowKey="id"
          scroll={{ x: 1200 }}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showTotal: (total) => `Total ${total} assets`
          }}
        />
      </Card>

      {/* Add/Edit Modal */}
      <Modal
        title={editingAsset ? 'Edit Asset' : 'Add New Asset'}
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
        }}
        footer={null}
        width={700}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          onValuesChange={handleFormChange}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="asset_name"
                label="Asset Name"
                rules={[{ required: true, message: 'Please enter asset name' }]}
              >
                <Input placeholder="e.g., Dell Laptop" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="asset_code"
                label="Asset Code (Auto-generated)"
                tooltip="Asset code will be auto-generated based on category and asset name"
              >
                <Input 
                  placeholder="Select category and enter asset name" 
                  readOnly 
                  style={{ 
                    backgroundColor: '#f5f5f5',
                    color: '#000',
                    cursor: 'not-allowed',
                    fontWeight: '500'
                  }}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="category_id"
                label="Category"
                rules={[{ required: true, message: 'Please select category' }]}
              >
                <Select placeholder="Select category">
                  {categories.map(cat => (
                    <Option key={cat.id} value={cat.id}>{cat.category_name}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="status"
                label="Status"
                rules={[{ required: true, message: 'Please select status' }]}
                initialValue="Active"
              >
                <Select placeholder="Select status">
                  {ASSET_STATUS.map(status => (
                    <Option key={status.value} value={status.value}>{status.label}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="brand"
                label="Brand"
              >
                <Input placeholder="e.g., Dell, HP" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="model"
                label="Model"
              >
                <Input placeholder="e.g., Latitude 5420" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="serial_number"
                label="Serial Number"
              >
                <Input placeholder="Serial number" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="purchase_price"
                label="Purchase Price (THB)"
                rules={[{ required: true, message: 'Please enter purchase price' }]}
              >
                <InputNumber
                  style={{ width: '100%' }}
                  min={0}
                  precision={2}
                  placeholder="0.00"
                  prefix="฿"
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="quantity"
                label="Quantity"
                rules={[{ required: true, message: 'Please enter quantity' }]}
                initialValue={1}
              >
                <InputNumber
                  style={{ width: '100%' }}
                  min={1}
                  precision={0}
                  placeholder="1"
                  onChange={(value) => {
                    const rate = form.getFieldValue('rate') || 0;
                    form.setFieldsValue({ amount: (value || 0) * rate });
                  }}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="rate"
                label="Rate per Unit (THB)"
                tooltip="Price per single unit"
              >
                <InputNumber
                  style={{ width: '100%' }}
                  min={0}
                  precision={2}
                  placeholder="0.00"
                  prefix="฿"
                  onChange={(value) => {
                    const qty = form.getFieldValue('quantity') || 0;
                    form.setFieldsValue({ amount: qty * (value || 0) });
                  }}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="amount"
                label="Total Amount (THB)"
                tooltip="Auto-calculated from Quantity × Rate"
              >
                <InputNumber
                  style={{ width: '100%' }}
                  min={0}
                  precision={2}
                  placeholder="0.00"
                  prefix="฿"
                  readOnly
                  disabled
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="current_value"
                label="Current Value (THB)"
                tooltip="Current depreciated value"
              >
                <InputNumber
                  style={{ width: '100%' }}
                  min={0}
                  precision={2}
                  placeholder="Same as purchase price"
                  prefix="฿"
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="depreciation_rate"
                label="Depreciation Rate (%)"
                tooltip="Annual depreciation percentage"
              >
                <InputNumber
                  style={{ width: '100%' }}
                  min={0}
                  max={100}
                  precision={2}
                  placeholder="0.00"
                  suffix="%"
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="purchase_date"
                label="Purchase Date"
              >
                <DatePicker
                  style={{ width: '100%' }}
                  format="DD/MM/YYYY"
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="warranty_expiry"
                label="Warranty Expiry"
              >
                <DatePicker
                  style={{ width: '100%' }}
                  format="DD/MM/YYYY"
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="condition"
                label="Condition"
                initialValue="Good"
              >
                <Select placeholder="Select condition">
                  {ASSET_CONDITION.map(cond => (
                    <Option key={cond.value} value={cond.value}>{cond.label}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="location"
                label="Location"
              >
                <Input placeholder="e.g., Office Floor 2" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="assigned_to"
                label="Assigned To (Employee)"
              >
                <Select
                  showSearch
                  allowClear
                  placeholder="Select employee"
                  filterOption={(input, option) => {
                    const label = option?.label || '';
                    return label.toLowerCase().includes(input.toLowerCase());
                  }}
                  options={employees.map(emp => ({
                    value: emp.id,
                    label: `${emp.english_first_name || ''} ${emp.english_last_name || ''}`.trim()
                  }))}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="last_maintenance_date"
                label="Last Maintenance Date"
              >
                <DatePicker
                  style={{ width: '100%' }}
                  format="DD/MM/YYYY"
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="next_maintenance_date"
                label="Next Maintenance Date"
              >
                <DatePicker
                  style={{ width: '100%' }}
                  format="DD/MM/YYYY"
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="description"
            label="Description"
          >
            <TextArea
              rows={3}
              placeholder="Additional details about the asset..."
            />
          </Form.Item>

          <Form.Item
            name="notes"
            label="Notes"
          >
            <TextArea
              rows={2}
              placeholder="Any additional notes..."
            />
          </Form.Item>

          <Form.Item>
            <Space style={{ float: 'right' }}>
              <Button onClick={() => setModalVisible(false)}>
                Cancel
              </Button>
              <Button type="primary" htmlType="submit" loading={loading}>
                {editingAsset ? 'Update' : 'Add'} Asset
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </Layout>
  );
}
